/**
 * AnimatedBadge Component
 * 
 * Displays an animated badge with various styles and animations
 */

import React from 'react'
import './AnimatedBadge.css'

export type BadgeStyle = 
  | 'DEFAULT'
  | 'GRADIENT_FIRE'
  | 'GRADIENT_OCEAN'
  | 'GRADIENT_SUNSET'
  | 'GRADIENT_FOREST'
  | 'GRADIENT_GALAXY'
  | 'NEON_BLUE'
  | 'NEON_PINK'
  | 'NEON_GREEN'
  | 'GOLD_SHINE'
  | 'SILVER_METALLIC'
  | 'RAINBOW'

export type BadgeAnimation = 
  | 'NONE'
  | 'PULSE'
  | 'BOUNCE'
  | 'GLOW'
  | 'SHAKE'
  | 'FLOAT'
  | 'SPIN'
  | 'WAVE'
  | 'HEARTBEAT'

interface AnimatedBadgeProps {
  name: string
  icon?: string
  style?: BadgeStyle
  animation?: BadgeAnimation
  className?: string
}

const getStyleClasses = (style: BadgeStyle): string => {
  const styles: Record<BadgeStyle, string> = {
    DEFAULT: 'bg-gray-600 text-white',
    GRADIENT_FIRE: 'bg-gradient-to-r from-red-600 via-orange-600 to-yellow-500 text-white font-bold',
    GRADIENT_OCEAN: 'bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-400 text-white font-bold',
    GRADIENT_SUNSET: 'bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white font-bold',
    GRADIENT_FOREST: 'bg-gradient-to-r from-green-700 via-emerald-600 to-lime-500 text-white font-bold',
    GRADIENT_GALAXY: 'bg-gradient-to-r from-indigo-900 via-purple-700 to-pink-600 text-white font-bold',
    NEON_BLUE: 'bg-blue-500 text-white font-bold shadow-[0_0_15px_rgba(59,130,246,0.7)]',
    NEON_PINK: 'bg-pink-500 text-white font-bold shadow-[0_0_15px_rgba(236,72,153,0.7)]',
    NEON_GREEN: 'bg-green-500 text-white font-bold shadow-[0_0_15px_rgba(34,197,94,0.7)]',
    GOLD_SHINE: 'bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 text-yellow-950 font-bold shadow-lg',
    SILVER_METALLIC: 'bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500 text-gray-900 font-bold shadow-lg',
    RAINBOW: 'bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500 text-white font-extrabold',
  }
  return styles[style] || styles.DEFAULT
}

const getAnimationClasses = (animation: BadgeAnimation): string => {
  const animations: Record<BadgeAnimation, string> = {
    NONE: '',
    PULSE: 'animate-pulse-custom',
    BOUNCE: 'animate-bounce-custom',
    GLOW: 'animate-glow',
    SHAKE: 'animate-shake',
    FLOAT: 'animate-float',
    SPIN: 'animate-spin-slow',
    WAVE: 'animate-wave',
    HEARTBEAT: 'animate-heartbeat',
  }
  return animations[animation] || ''
}

export default function AnimatedBadge({
  name,
  icon,
  style = 'DEFAULT',
  animation = 'NONE',
  className = '',
}: AnimatedBadgeProps) {
  const styleClass = getStyleClasses(style)
  const animationClass = getAnimationClasses(animation)

  return (
    <div
      className={`
        inline-flex items-center gap-1.5 px-3 py-1 rounded-full 
        transition-all duration-300
        ${styleClass} 
        ${animationClass}
        ${className}
      `}
    >
      {icon && <span className="text-base">{icon}</span>}
      <span className="text-sm font-medium">{name}</span>
    </div>
  )
}
